// apps/files-manager/src/layouts/FileManagerLayout.jsx
import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  FolderOpen,
  Package,
  BookOpen,
  FileText,
  Calculator,
  Video,
  Image,
  Building2,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  ExternalLink,
  Briefcase,
  Layers,
  Megaphone,
  ShoppingBag,
} from 'lucide-react';
import { cn } from '../lib/utils';

const LOGO_URL = 'https://files.housedesigns99.com/api/files/f5eolh49d789mqi/rwth7jtsmlcst1n/house_designs99_logo_fwjsybt4i7.png';

const FileManagerLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path) && path !== '/dashboard';
  };

  const menuGroups = [
    {
      title: 'Content',
      icon: Layers,
      iconColor: 'text-blue-400',
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, color: 'text-blue-400' },
        { path: '/portfolio', label: 'Portfolio', icon: FolderOpen, color: 'text-cyan-400' },
        { path: '/products', label: 'Products', icon: Package, color: 'text-amber-400' },
        { path: '/store', label: 'Store', icon: ShoppingBag, color: 'text-orange-400' },
        { path: '/ebooks', label: 'E-Books', icon: BookOpen, color: 'text-purple-400' },
        { path: '/blogs', label: 'Blogs', icon: FileText, color: 'text-indigo-400' },
        { path: '/videos', label: 'Videos', icon: Video, color: 'text-rose-400' },
        { path: '/gallery', label: 'Gallery', icon: Image, color: 'text-pink-400' },
      ],
    },
    {
      title: 'Management',
      icon: Briefcase,
      iconColor: 'text-amber-400',
      items: [
        { path: '/calculators', label: 'Calculators', icon: Calculator, color: 'text-emerald-400' },
        { path: '/properties', label: 'Properties', icon: Building2, color: 'text-orange-400' },
        { path: '/customers', label: 'Customers', icon: Users, color: 'text-green-400' },
        { path: '/settings', label: 'Settings', icon: Settings, color: 'text-slate-400' },
      ],
    },
    {
      title: 'Advertising',
      icon: Megaphone,
      iconColor: 'text-amber-400',
      items: [
        { path: '/ads', label: 'Advertisements', icon: Megaphone, color: 'text-amber-400' },
      ],
    },
  ];

  return (
    <div className="h-screen overflow-hidden bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm px-6 py-3 flex items-center justify-between border-b border-slate-200 sticky top-0 z-50 shrink-0">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Logo" className="w-8 h-8 rounded-lg object-contain" />
          <span className="text-xl font-bold text-slate-800">
            Files <span className="text-amber-500">Manager</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-700">{user?.name || 'Admin'}</span>
          <span className="text-xs bg-slate-200 text-slate-600 px-2.5 py-1 rounded-full font-medium">
            {user?.role || 'admin'}
          </span>
          <button
            onClick={handleLogout}
            className="text-slate-400 hover:text-red-500 transition text-sm px-2 py-1 hover:bg-red-50 rounded-lg"
          >
            ⏻
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Mobile Menu Button */}
        <button
          className="lg:hidden fixed bottom-4 left-4 z-50 p-2 bg-white rounded-md shadow"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-6 w-6" />
        </button>

        {/* Sidebar */}
        <aside
          className={cn(
            "fixed inset-y-0 left-0 z-40 w-72 bg-gradient-to-b from-slate-900 via-gray-900 to-black text-white shadow-2xl transform transition-transform duration-300 lg:translate-x-0 flex flex-col",
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="flex justify-between items-center p-5 border-b border-white/10 shrink-0">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Logo" className="w-8 h-8 rounded-lg object-contain" />
              <h1 className="text-xl font-bold tracking-tight">Files Manager</h1>
            </div>
            <button className="lg:hidden p-1 text-white/70 hover:text-white" onClick={() => setSidebarOpen(false)}>
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-4 py-3 border-b border-white/10 shrink-0">
            <p className="text-xs font-medium text-indigo-300 uppercase tracking-wider">Logged in as</p>
            <p className="font-semibold text-white">
              {user?.name || 'Admin'} <span className="text-indigo-300 text-sm">({user?.role || 'admin'})</span>
            </p>
          </div>

          {/* Navigation */}
          <div className="flex-1 min-h-0 overflow-y-auto px-3 py-4">
            {menuGroups.map((group, idx) => (
              <div key={idx} className="mb-4">
                <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-indigo-300">
                  <group.icon className={`w-4 h-4 ${group.iconColor || 'text-indigo-300'}`} />
                  <span>{group.title}</span>
                </div>
                <div className="space-y-1 mt-1">
                  {group.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200",
                        isActive(item.path)
                          ? "bg-amber-500/20 text-amber-300 shadow-lg shadow-amber-500/10 border-r-4 border-amber-400"
                          : "text-indigo-200 hover:bg-white/10 hover:text-white"
                      )}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className={`w-5 h-5 ${item.color || 'text-indigo-300'} flex-shrink-0`} />
                      <span className="whitespace-nowrap">{item.label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="border-t border-white/10 p-3 shrink-0 space-y-2">
            <a
              href="https://housedesigns99.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-3 py-2 text-indigo-200 hover:bg-white/10 hover:text-white rounded-lg transition"
            >
              <ExternalLink className="w-5 h-5 flex-shrink-0" />
              <span className="whitespace-nowrap">Homepage</span>
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2 w-full text-red-300 hover:bg-red-500/20 hover:text-red-200 rounded-lg transition"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              <span className="whitespace-nowrap">Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-h-0 overflow-y-auto p-6 lg:ml-72">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default FileManagerLayout;